/**
 * Zoho Books Service
 * Handles contact import from Zoho Books
 */

import api from './api';
import { Customer } from '../types';

export interface ZohoContact {
  id: string;
  contactId: string;
  contactName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  billingAddress?: string;
  shippingAddress?: string;
  gstNo?: string;
  taxId?: string;
  currency?: string;
  paymentTerms?: string;
  creditLimit?: number;
  outstandingReceivable?: number;
  syncDisposition?: 'NEW' | 'LINKED' | 'DUPLICATE' | 'REVIEW' | 'SKIPPED';
  syncMatchMethod?:
    | 'ZOHO_CONTACT_ID'
    | 'EMAIL_EXACT'
    | 'PHONE_EXACT'
    | 'COMPANY_NORMALIZED'
    | 'FUZZY'
    | 'NONE';
  syncReason?: string;
  matchedCustomerId?: string | null;
  matchedCustomerName?: string | null;
  matchedCustomerIds?: string[];
  matchedCustomers?: Array<{
    id: string;
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    company?: string | null;
    zohoContactId?: string | null;
  }>;
}

export interface ZohoPreviewSummary {
  newCount: number;
  linkedCount: number;
  duplicateCount: number;
  reviewCount: number;
  skippedCount: number;
}

export interface ZohoInitialImportSummary {
  alreadyLinked: number;
  exactEmailMatch: number;
  exactPhoneMatch: number;
  normalizedCompanyMatch: number;
  newCustomers: number;
  reviewCount: number;
  duplicateCount: number;
  fuzzyReviewCount: number;
  skippedCount: number;
}

export interface ZohoInitialImportSample {
  zohoName: string;
  zohoEmail: string | null;
  matchedCustomerName: string | null;
  matchedCustomerId: string | null;
  matchReason: string;
}

export interface ZohoInitialImportSamples {
  exactEmail: ZohoInitialImportSample[];
  companyMatch: ZohoInitialImportSample[];
  newCustomers: ZohoInitialImportSample[];
  review: ZohoInitialImportSample[];
  duplicate: ZohoInitialImportSample[];
}

export interface ZohoSyncResult {
  imported: number;
  updated: number;
  linked?: number;
  duplicate?: number;
  review?: number;
  skipped: number;
  errors?: Array<{ contact: string; error: string }>;
  results?: Array<{
    contactId: string;
    contactName: string;
    disposition: 'NEW' | 'LINKED' | 'DUPLICATE' | 'REVIEW' | 'SKIPPED';
    matchMethod: string;
    customerId?: string | null;
    customerName?: string | null;
    reason: string;
  }>;
}

export interface ZohoSyncApprovalPlan {
  selectedNewContactIds?: string[];
  selectedLinkedContactIds?: string[];
  selectedReviewContactIds?: string[];
  skippedContactIds?: string[];
}

export interface ZohoInitialImportResult {
  imported: number;
  linked: number;
  updated: number;
  review: number;
  reviewOnly?: number;
  duplicate?: number;
  skipped: number;
  errors?: Array<{ contact: string; error: string }>;
  results?: ZohoSyncResult['results'];
  message?: string;
}

type ZohoInitialImportJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

interface ZohoInitialImportJobResponse {
  jobId: string;
  status: ZohoInitialImportJobStatus;
  summary?: ZohoInitialImportSummary | null;
  samples?: ZohoInitialImportSamples | null;
  result?: ZohoInitialImportResult | null;
  error?: string | null;
}

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

async function pollInitialImportJob<T>(
  load: () => Promise<ZohoInitialImportJobResponse>,
  pick: (job: ZohoInitialImportJobResponse) => T | null | undefined,
  timeoutMs = 5 * 60 * 1000
): Promise<T> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const job = await load();
    if (job.status === 'COMPLETED') {
      const payload = pick(job);
      if (payload == null) {
        throw new Error('Zoho initial import job completed without a result payload');
      }
      return payload;
    }
    if (job.status === 'FAILED') {
      throw new Error(job.error || 'Zoho initial import job failed');
    }
    await wait(2000);
  }

  throw new Error('Zoho initial import is still running. Please try again in a moment.');
}

export const zohoCRMService = {
  /**
   * Preview contacts from Zoho Books (limited to 10)
   */
  previewContacts: async (
    source: 'BOOKS' | 'CRM' | 'AUTO' = 'BOOKS'
  ): Promise<{ contacts: ZohoContact[]; summary: ZohoPreviewSummary | null }> => {
    const params = `?source=${source}`;
    const response = await api.get(`/customers/zoho/preview${params}`);
    return {
      contacts: response.data?.data || [],
      summary: response.data?.summary || null,
    };
  },

  /**
   * Search contacts from Zoho Books by name or email
   */
  searchContacts: async (
    query: string,
    source: 'BOOKS' | 'CRM' | 'AUTO' = 'BOOKS'
  ): Promise<{ contacts: ZohoContact[]; summary: ZohoPreviewSummary | null }> => {
    if (!query || query.trim().length < 2) {
      return { contacts: [], summary: null };
    }
    const params = `?q=${encodeURIComponent(query)}&source=${source}`;
    const response = await api.get(`/customers/zoho/search${params}`);
    return {
      contacts: response.data?.data || [],
      summary: response.data?.summary || null,
    };
  },

  /**
   * Onboard a single contact from Zoho Books
   */
  onboardContact: async (contactId: string, categoryId?: string): Promise<Customer> => {
    const response = await api.post('/customers/zoho/onboard', { contactId, categoryId });
    return response.data.customer;
  },

  /**
   * Sync all contacts from Zoho Books
   */
  syncContacts: async (options: {
    source?: 'BOOKS' | 'CRM' | 'AUTO';
    updateExisting?: boolean;
    overwriteLocalFields?: boolean;
    selectedNewContactIds?: string[];
    selectedLinkedContactIds?: string[];
    selectedReviewContactIds?: string[];
    skippedContactIds?: string[];
    categoryId?: string;
  }): Promise<ZohoSyncResult> => {
    // Default to Books if not specified
    const syncOptions = { ...options, source: options.source || 'BOOKS' };
    const response = await api.post('/customers/zoho/sync', syncOptions);
    return response.data;
  },

  previewInitialImport: async (
    source: 'BOOKS' | 'CRM' | 'AUTO' = 'BOOKS'
  ): Promise<{ contacts: ZohoContact[]; summary: ZohoInitialImportSummary | null; samples: ZohoInitialImportSamples | null }> => {
    const response = await api.post('/customers/zoho/initial-import-preview', { source });
    const jobId = String(response.data?.jobId || '').trim();
    if (!jobId) {
      throw new Error('Zoho initial import preview did not return a job id');
    }
    const job = await pollInitialImportJob(
      async () => {
        const pollResponse = await api.get(`/customers/zoho/initial-import-preview/${jobId}`);
        return pollResponse.data as ZohoInitialImportJobResponse;
      },
      (job) => job
    );
    return {
      contacts: [],
      summary: job.summary || null,
      samples: job.samples || null,
    };
  },

  runInitialImport: async (
    source: 'BOOKS' | 'CRM' | 'AUTO' = 'BOOKS'
  ): Promise<ZohoInitialImportResult> => {
    const response = await api.post('/customers/zoho/initial-import', { source });
    const jobId = String(response.data?.jobId || '').trim();
    if (!jobId) {
      throw new Error('Zoho initial import did not return a job id');
    }
    return pollInitialImportJob(
      async () => {
        const pollResponse = await api.get(`/customers/zoho/initial-import/${jobId}`);
        return pollResponse.data as ZohoInitialImportJobResponse;
      },
      (job) => job.result || null,
      15 * 60 * 1000
    );
  },
};
